import axios from '../../../libs/axios_csrf';
import moment from 'moment';
import { extendObservable, computed, action } from 'mobx';
import { validateEmail } from '../functions';
import { injectIntl } from 'react-intl';
import { inactiveMembersKey } from '../constants';

export default class MembersStore {
  constructor(rootStore, memberships, features, permissions) {
    this.rootStore = rootStore;
    extendObservable(this, {
      features: features,
      memberships: memberships,
      canManage: permissions['can_manage_memberships'],
      canShow: permissions['can_show_memberships'],

      // Profile display info
      isProfileLoading: false,
      showProfileModal: false,
      profileId: null,
      profileMember: null
    });

    this.handleRole = this.handleRole.bind(this);
    this.handleSkill = this.handleSkill.bind(this);
    this.handleResponsibility = this.handleResponsibility.bind(this);
    this.getBoard = this.getBoard.bind(this);
    this.handleBoard = this.handleBoard.bind(this);
    this.confirm = this.confirm.bind(this);
  }

  confirm() {
    return confirm(
      this.rootStore.localeStore.intl.formatMessage({ id: 'are_you_sure' })
    );
  }

  getMember(membershipsID) {
    return this.memberships.find(e => e.id === membershipsID);
  }

  getDirector() {
    return this.memberships.find(e => e.subdirector === true);
  }

  @action
  toggleShowModal(member) {
    const id = member != undefined ? member.user.profile_id : null;
    this.showProfileModal = !this.showProfileModal;
    this.profileId = this.showProfileModal ? id : null;
    this.profileMember = member;
    this.isProfileLoading = true;

    if (this.showProfileModal) this.getMemberProfile();
    else this.currentProfileData = null;
  }

  @action
  async getMemberProfile() {
    try {
      const response = await axios.get(`/profiles/${this.profileId}.json`);
      this.currentProfileData = response.data;
      this.isProfileLoading = false;
    } catch (error) {
      console.log(error);
    }
  }

  @computed
  get members() {
    let membersObject = {};
    this.memberships.forEach(m => {
      if (!m.active)
        membersObject[inactiveMembersKey]
          ? membersObject[inactiveMembersKey].push(m)
          : (membersObject[inactiveMembersKey] = [m]);
      else {
        membersObject[m.role]
          ? membersObject[m.role].push(m)
          : (membersObject[m.role] = [m]);

        let r = this.getBoard(m.id);
        if (r.includes('director')) r.push('board_of_director');

        if (r.length > 0)
          r.map(elem => {
            membersObject[elem]
              ? membersObject[elem].push(m)
              : (membersObject[elem] = [m]);
          });
      }
    });
    return membersObject;
  }

  @computed
  get boards() {
    let boardsObject = {};
    this.memberships.forEach(m => {
      boardsObject[m.role]
        ? boardsObject[m.role].push(m)
        : (boardsObject[m.role] = [m]);
    });
    return boardsObject;
  }

  patch(prop, membershipsID, atr) {
    axios
      .patch(`/memberships/` + membershipsID, { [prop]: atr })
      .then(response => {
        this.rootStore.generateAlert({
          type: 'success',
          headline: 'successful_update'
        });
      })
      .catch(e => console.log(e));
  }

  handleActive(membershipsID, atr) {
    let current = this.getMember(membershipsID);
    current.active = atr;
    this.patch('active', membershipsID, atr);
  }

  handleCustomerSupport(membershipsID, atr) {
    let current = this.getMember(membershipsID);
    current.customer_support_leader = atr;
    this.patch('customer_support_leader', membershipsID, atr);
  }

  handleDirector(membershipsID, atr) {
    let previousDirector = this.getDirector();
    if (previousDirector) {
      previousDirector.subdirector = false;
      axios
        .patch(`/memberships/` + previousDirector.id, { subdirector: false })
        .then(response => {})
        .catch(e => console.log(e));
    }

    let current = this.getMember(membershipsID);
    current.subdirector = atr;
    this.patch('subdirector', membershipsID, atr);
  }

  handleRole(membershipsID, atr) {
    if (atr === 'ceo') {
      let oldCEO = this.memberships.find(e => e.role === 'ceo');
      if (oldCEO) {
        oldCEO.role = 'co_founder';
      }
    }

    let current = this.getMember(membershipsID);
    current.role = atr;
    this.patch('role', membershipsID, atr);
  }

  handleResponsibility(membershipsID, atr) {
    let current = this.getMember(membershipsID);
    current.scorecard = atr;
    this.patch('scorecard', membershipsID, atr);
  }

  getBoard(membershipsID) {
    let current = this.getMember(membershipsID);
    let { advisory_board, advisory_service, director } = current;

    return ['advisory_board', 'advisory_service', 'director'].filter(
      item => current[item]
    );
  }

  @action
  handleBoard(membershipsID, atr) {
    let current = this.getMember(membershipsID);
    let self = this;

    function board(atr1, atr2, atr3) {
      current.advisory_board = atr1;
      current.advisory_service = atr2;
      current.director = atr3;
      axios
        .patch(`/memberships/` + membershipsID, {
          advisory_board: atr1,
          advisory_service: atr2,
          director: atr3
        })
        .then(response => {
          self.rootStore.generateAlert({
            type: 'success',
            headline: 'successful_update'
          });
        })
        .catch(e => console.log(e));
    }

    if (atr === 'advisory_board') {
      board(
        !current.advisory_board,
        current.advisory_service,
        current.director
      );
    } else if (atr === 'advisory_service') {
      board(
        current.advisory_board,
        !current.advisory_service,
        current.director
      );
    } else if (atr === 'board_of_director') {
      board(
        current.advisory_board,
        current.advisory_service,
        !current.director
      );
    } else {
      board(false, false, false);
    }
  }

  handleSkill(membershipsID, attr) {
    let current = this.getMember(membershipsID);
    current.skill = attr;
    this.patch('skill', membershipsID, attr);
  }

  getCustomSkill(id) {
    const customSkill = this.features.customSkills.find(
      skill => skill.id == id
    );

    return customSkill;
  }

  handleCustomSkill(membershipsID, id) {
    let current = this.getMember(membershipsID);
    current.custom_skill_id = id;

    this.patch('custom_skill_id', membershipsID, id);
  }

  async createCustomSkill(membershipsID, attr) {
    var newId = null;
    await axios
      .post('/custom_skills.json', { custom_skill: { name: attr } })
      .then(response => {
        this.features.customSkills.push(response.data);
        this.handleCustomSkill(membershipsID, response.data.id);
        newId = response.data.id;
        return newId;
      })
      .catch(error => console.log('Custom skill error: CREATE'));
  }

  editCustomSkill(params) {
    axios
      .patch(`/custom_skills/${params.id}`, params)
      .then(response => {
        const index = this.features.customSkills.findIndex(
          skill => skill.id == response.data.id
        );
        this.features.customSkills[index] = response.data;
        this.rootStore.generateAlert({
          type: 'success',
          headline: 'successful_update'
        });
      })
      .catch(error => console.log('Custom skill error: UPDATE'));
  }

  deleteCustomSkill(params) {
    axios
      .delete(`/custom_skills/${params.id}`, params)
      .then(response => {
        this.features.customSkills = this.features.customSkills.filter(
          skill => skill.id != params.id
        );
        this.rootStore.generateAlert({
          type: 'success',
          headline: 'successful_update'
        });
      })
      .catch(error => console.log('Custom skill: DELETE'));
  }
}
